import { parse as parseYaml } from 'yaml';
import { validateManifest } from 'fargate-deployer/dist/lib/validate';
import type { ManifestIssue } from 'fargate-deployer/dist/lib/errors';

/**
 * Validation, in the browser, using the action's own validator.
 *
 * This imports `fargate-deployer/dist/lib/validate` rather than the package
 * root: the root re-exports the CDK stacks, which would drag `aws-cdk-lib` into
 * the bundle. The validator itself depends only on `errors` and `types`, so it
 * runs anywhere — which is what makes the page able to give exactly the errors
 * the CLI would, with no server and no AWS credentials.
 *
 * It deliberately stops at validation. Resolving a manifest needs `naming.ts`,
 * which hashes with node's `crypto`, and synthesising needs CDK — neither
 * belongs in a browser tab.
 */

export type Result =
  | { status: 'empty' }
  | { status: 'valid'; summary: Summary }
  | { status: 'invalid'; issues: ManifestIssue[] }
  | { status: 'unparseable'; message: string };

export interface Summary {
  kind: string;
  name: string;
  detail: string[];
}

export function validate(text: string): Result {
  if (text.trim() === '') {
    return { status: 'empty' };
  }

  let parsed: unknown;
  try {
    parsed = parseYaml(text);
  } catch (error) {
    return {
      status: 'unparseable',
      message: error instanceof Error ? error.message : String(error),
    };
  }

  if (parsed === null || parsed === undefined) {
    return { status: 'unparseable', message: 'The document is empty.' };
  }

  try {
    const manifest = validateManifest(parsed, 'manifest.yaml') as any;
    return { status: 'valid', summary: summarise(manifest) };
  } catch (error) {
    const issues = (error as { issues?: ManifestIssue[] }).issues;
    if (issues) {
      return { status: 'invalid', issues };
    }
    return {
      status: 'unparseable',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

/** A plain-language read of what the manifest would deploy. */
function summarise(m: any): Summary {
  const detail: string[] = [
    `${m.account} · ${m.region} · cluster ${m.cluster?.name}`,
    `${m.task?.cpu} CPU units / ${m.task?.memory} MiB`,
  ];

  if (m.kind === 'Service') {
    detail.push(`${m.service?.desiredCount ?? 1} task${(m.service?.desiredCount ?? 1) === 1 ? '' : 's'}`);

    const lb = m.loadBalancer;
    if (!lb || lb.enabled === false) {
      detail.push('no load balancer');
    } else if (lb.defaultAction) {
      detail.push('listener default action');
    } else {
      const hosts = [lb.hostHeaders ?? []].flat();
      const paths = [lb.pathPatterns ?? []].flat();
      detail.push([...hosts, ...paths].join(', '));
    }

    if (m.autoScaling && m.autoScaling.enabled !== false) {
      const targets = [
        m.autoScaling.cpuTargetPercent !== undefined && `CPU ${m.autoScaling.cpuTargetPercent}%`,
        m.autoScaling.memoryTargetPercent !== undefined && `memory ${m.autoScaling.memoryTargetPercent}%`,
        m.autoScaling.requestsPerTarget !== undefined && `${m.autoScaling.requestsPerTarget} req/task`,
      ].filter(Boolean);
      detail.push(
        `scales ${m.autoScaling.minCapacity ?? m.service?.desiredCount ?? 1}–${m.autoScaling.maxCapacity} on ${targets.join(' + ')}`,
      );
    } else {
      detail.push('fixed task count');
    }
  } else {
    const jobs = m.tasks ?? [];
    const off = jobs.filter((t: any) => t.enabled === false).length;
    detail.push(`${jobs.length} job${jobs.length === 1 ? '' : 's'}${off ? `, ${off} disabled` : ''}`);
    const sizes = new Set(jobs.map((t: any) => `${t.cpu ?? m.task?.cpu}/${t.memory ?? m.task?.memory}`));
    detail.push(`${sizes.size} task definition${sizes.size === 1 ? '' : 's'}`);
  }

  return { kind: m.kind, name: m.name, detail };
}
